import React, { useEffect, useState, useCallback } from 'react';
import { Table, Button, Form, Input, Select, message, Space, Tag, Row, Col, Card, Modal, Alert } from 'antd';
import { PlusOutlined, EyeOutlined, ExperimentOutlined } from '@ant-design/icons';
import { patientService } from '../../services';
import { testService } from '../../services/testService';
import { labTestSelectOptions, labOrderEntries, labOrderTotal } from '../../utils/labOrder';
import { useTranslation } from 'react-i18next';
import { lifestyleOptions } from '../../utils/lifestyle';
import DataTransfer from '../../components/DataTransfer';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import SearchFilter from '../../components/SearchFilter';
import FormModal from '../../components/FormModal';
import DuplicatePatientModal from '../../components/DuplicatePatientModal';
import dayjs from 'dayjs';
import './Patients.css';

const Patients = () => {
    const [patients, setPatients] = useState([]);
    const [filteredPatients, setFilteredPatients] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    // Existing patients matching what is being typed into the create form.
    const [duplicates, setDuplicates] = useState([]);
    const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
    const [pendingValues, setPendingValues] = useState(null);
    const [creating, setCreating] = useState(false);
    const [form] = Form.useForm();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { user } = useAuth();

    // Branch doctors can order lab tests directly from the patient list, choosing
    // from the branch lab's catalog. Independent doctors (no branchId) don't see this.
    // Any doctor can order: a branch doctor from the branch lab's catalog, an
    // independent one from their own lab's, and either from the library they
    // switched on in settings. Gating this on `branchId` used to hide the button
    // from every doctor without a hospital, including those running their own lab.
    const canOrderLab = user?.role === 'doctor';
    const [orderModalVisible, setOrderModalVisible] = useState(false);
    const [orderPatient, setOrderPatient] = useState(null);
    const [catalog, setCatalog] = useState([]);
    const [selectedCatalogIds, setSelectedCatalogIds] = useState([]);
    const [orderSubmitting, setOrderSubmitting] = useState(false);
    const [orderForm] = Form.useForm();

    useEffect(() => {
        fetchPatients();
    }, []);

    const fetchCatalog = useCallback(async () => {
        if (!canOrderLab) return;
        try {
            const res = await testService.getBranchCatalog();
            setCatalog(res.data || []);
        } catch (error) {
            // Non-fatal: the order modal just shows an empty list.
            console.error('Failed to load lab catalog:', error.message);
        }
    }, [canOrderLab]);

    useEffect(() => {
        fetchCatalog();
    }, [fetchCatalog]);

    const openOrderModal = (patient) => {
        setOrderPatient(patient);
        orderForm.resetFields();
        setSelectedCatalogIds([]);
        setOrderModalVisible(true);
    };

    const selectedTests = selectedCatalogIds
        .map((value) => catalog.find((entry) => entry.value === value))
        .filter(Boolean);
    // Only the lab's own rows carry a price; a library test has none until a lab
    // prices it, so the total is over the catalog rows alone.
    const totalPrice = labOrderTotal(selectedCatalogIds, catalog);

    // A doctor normally requests a panel, so every picked test is sent to the lab
    // as its own order in a single request.
    const handleOrderSubmit = async (values) => {
        if (selectedCatalogIds.length === 0) {
            message.error(t('selectTest') || 'Please select a test');
            return;
        }
        setOrderSubmitting(true);
        try {
            const response = await testService.orderTests({
                patientId: orderPatient._id,
                notes: values.notes,
                tests: labOrderEntries(selectedCatalogIds, catalog)
            });
            const count = response?.count || selectedCatalogIds.length;
            message.success(
                `${t('testOrderedSuccessfully') || 'Lab test ordered'} (${count})`
            );
            setOrderModalVisible(false);
            orderForm.resetFields();
            setSelectedCatalogIds([]);
        } catch (error) {
            message.error(error.message || t('error'));
        } finally {
            setOrderSubmitting(false);
        }
    };

    const fetchPatients = async () => {
        setLoading(true);
        try {
            const response = await patientService.getPatients();
            setPatients(response.data);
            setFilteredPatients(response.data);
        } catch (error) {
            message.error(error.response?.data?.message || t('error'));
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (values) => {
        let filtered = patients;

        if (values.name) {
            filtered = filtered.filter(p =>
                p.name.toLowerCase().includes(values.name.toLowerCase())
            );
        }

        if (values.phoneNumber) {
            filtered = filtered.filter(p =>
                p.phoneNumber.includes(values.phoneNumber)
            );
        }

        if (values.gender) {
            filtered = filtered.filter(p => p.gender === values.gender);
        }

        if (values.bloodType) {
            filtered = filtered.filter(p => p.bloodType === values.bloodType);
        }

        setFilteredPatients(filtered);
    };

    const handleReset = () => {
        setFilteredPatients(patients);
    };

    const handleCreate = () => {
        form.resetFields();
        setDuplicates([]);
        setModalVisible(true);
    };

    // Looked up while the form is still open, so a returning patient is spotted
    // before anything is saved. Silent on failure - this is a convenience, and it
    // must never stop a registration going through.
    const lookForDuplicates = async () => {
        const { name, phoneNumber } = form.getFieldsValue(['name', 'phoneNumber']);
        if (!name && !phoneNumber) return;
        try {
            const res = await patientService.checkDuplicate({ name, phoneNumber });
            setDuplicates(res.data || []);
        } catch (error) {
            console.error('Duplicate check failed:', error.message);
        }
    };

    const openExistingPatient = (patient) => {
        setDuplicateModalOpen(false);
        setModalVisible(false);
        navigate(`/doctor/patient/${patient._id}`);
    };

    const submitPatient = async (values, { force = false } = {}) => {
        setCreating(true);
        try {
            await patientService.createPatient({ ...values, force });
            message.success(t('success'));
            setDuplicateModalOpen(false);
            setModalVisible(false);
            setDuplicates([]);
            form.resetFields();
            fetchPatients();
        } catch (error) {
            // 409 = this person already has a file; offer their profile instead.
            if (error.response?.status === 409) {
                setPendingValues(values);
                setDuplicates(error.response.data?.data || []);
                setDuplicateModalOpen(true);
            } else {
                message.error(error.response?.data?.message || t('error'));
            }
        } finally {
            setCreating(false);
        }
    };

    const handleSubmit = (values) => submitPatient(values);

    const columns = [
        {
            title: t('name'),
            dataIndex: 'name',
            key: 'name'
        },
        {
            title: t('phoneNumber'),
            dataIndex: 'phoneNumber',
            key: 'phoneNumber'
        },
        {
            title: t('gender'),
            dataIndex: 'gender',
            key: 'gender',
            render: (gender) => gender ? t(gender) : '-'
        },
        {
            title: t('bloodType'),
            dataIndex: 'bloodType',
            key: 'bloodType',
            render: (type) => type || '-'
        },
        {
            title: t('createdBy'),
            dataIndex: ['createdBy', 'name'],
            key: 'createdBy',
            render: (name, record) => (
                <span>
                    {name} <Tag color={record.createdBy?.role === 'doctor' ? 'blue' : 'green'}>
                        {record.createdBy?.role}
                    </Tag>
                </span>
            )
        },
        {
            title: t('createdAt'),
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (date) => dayjs(date).format('YYYY-MM-DD')
        },
        {
            title: t('actions'),
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button
                        icon={<EyeOutlined />}
                        onClick={() => navigate(`/doctor/patient/${record._id}`)}
                    >
                        {t('view')}
                    </Button>
                    {canOrderLab && (
                        <Button
                            icon={<ExperimentOutlined />}
                            onClick={() => openOrderModal(record)}
                        >
                            {t('orderLabTest') || 'Order Lab Test'}
                        </Button>
                    )}
                </Space>
            )
        }
    ];

    return (
        <div className="page-container">
            <Row gutter={[16, 16]} align="middle" justify="space-between" className="page-header">
                <Col xs={24} sm={16} md={12}>
                    <h1 className="page-title">{t('patients')}</h1>
                </Col>
                <Col xs={24} sm={8} md={12} style={{ textAlign: 'right' }}>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleCreate}
                        size="large"
                        block={false}
                    >
                        {t('createPatient')}
                    </Button>
                </Col>
                <Col xs={24}>
                    <DataTransfer section="patients" size="small" onImported={fetchPatients} />
                </Col>
            </Row>

            <Card className="search-filter-card">
                <SearchFilter
                    searchFields={[
                        { key: 'name', label: t('name'), placeholder: t('searchBy') + ' ' + t('name') },
                        { key: 'phoneNumber', label: t('phoneNumber'), placeholder: t('searchBy') + ' ' + t('phoneNumber') }
                    ]}
                    filterFields={[
                        {
                            key: 'gender',
                            label: t('gender'),
                            options: [
                                { label: t('male'), value: 'male' },
                                { label: t('female'), value: 'female' },
                                { label: t('other'), value: 'other' }
                            ]
                        },
                        {
                            key: 'bloodType',
                            label: t('bloodType'),
                            options: [
                                { label: 'A+', value: 'A+' },
                                { label: 'A-', value: 'A-' },
                                { label: 'B+', value: 'B+' },
                                { label: 'B-', value: 'B-' },
                                { label: 'AB+', value: 'AB+' },
                                { label: 'AB-', value: 'AB-' },
                                { label: 'O+', value: 'O+' },
                                { label: 'O-', value: 'O-' }
                            ]
                        }
                    ]}
                    onSearch={handleSearch}
                    onReset={handleReset}
                />
            </Card>

            <Card className="table-card">
                <Table
                    columns={columns}
                    dataSource={filteredPatients}
                    rowKey="_id"
                    loading={loading}
                    scroll={{ x: 800 }}
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showTotal: (total) => `${t('total')}: ${total}`,
                    }}
                />
            </Card>

            <FormModal
                title={t('createPatient')}
                visible={modalVisible}
                onCancel={() => setModalVisible(false)}
                onSubmit={handleSubmit}
                form={form}
                width={700}
                className="form-modal-two-column"
            >
                {duplicates.length > 0 && (
                    <Alert
                        type="warning"
                        showIcon
                        style={{ marginBottom: 16 }}
                        message={t('patientAlreadyExists') || 'This patient already exists'}
                        description={
                            <Space direction="vertical" size={4}>
                                {duplicates.map((p) => (
                                    <Button
                                        key={p._id}
                                        type="link"
                                        style={{ padding: 0, height: 'auto' }}
                                        onClick={() => openExistingPatient(p)}
                                    >
                                        {p.name} — {p.phoneNumber} ({t('openProfile') || 'open profile'})
                                    </Button>
                                ))}
                            </Space>
                        }
                    />
                )}

                <Form.Item
                    name="name"
                    label={t('name')}
                    rules={[{ required: true, message: t('name') + ' ' + t('required') }]}
                >
                    <Input placeholder={t('name')} onBlur={lookForDuplicates} />
                </Form.Item>

                <Form.Item
                    name="phoneNumber"
                    label={t('phoneNumber')}
                    rules={[{ required: true, message: t('phoneNumber') + ' ' + t('required') }]}
                >
                    <Input placeholder={t('phoneNumber')} onBlur={lookForDuplicates} />
                </Form.Item>

                <Form.Item name="email" label={t('email')}>
                    <Input type="email" placeholder={t('email')} />
                </Form.Item>

                <Form.Item name="dateOfBirth" label={t('dateOfBirth')}>
                    <Input type="date" />
                </Form.Item>

                <Form.Item name="gender" label={t('gender')}>
                    <Select placeholder={t('select')}>
                        <Select.Option value="male">{t('male')}</Select.Option>
                        <Select.Option value="female">{t('female')}</Select.Option>
                        <Select.Option value="other">{t('other')}</Select.Option>
                    </Select>
                </Form.Item>

                <Form.Item name="bloodType" label={t('bloodType')}>
                    <Select placeholder={t('select')}>
                        <Select.Option value="A+">A+</Select.Option>
                        <Select.Option value="A-">A-</Select.Option>
                        <Select.Option value="B+">B+</Select.Option>
                        <Select.Option value="B-">B-</Select.Option>
                        <Select.Option value="AB+">AB+</Select.Option>
                        <Select.Option value="AB-">AB-</Select.Option>
                        <Select.Option value="O+">O+</Select.Option>
                        <Select.Option value="O-">O-</Select.Option>
                    </Select>
                </Form.Item>

                <Form.Item name="occupation" label={t('occupation')}>
                    <Input placeholder={t('occupation')} />
                </Form.Item>

                <Form.Item name="smoker" label={t('smoker')}>
                    <Select placeholder={t('select')} options={lifestyleOptions(t)} />
                </Form.Item>

                <Form.Item name="alcoholic" label={t('alcoholic')}>
                    <Select placeholder={t('select')} options={lifestyleOptions(t)} />
                </Form.Item>

                <Form.Item
                    name="address"
                    label={t('address')}
                    className="ant-form-item-full"
                >
                    <Input.TextArea placeholder={t('address')} rows={3} />
                </Form.Item>

                <Form.Item
                    name="allergies"
                    label={t('allergies')}
                    className="ant-form-item-full"
                >
                    <Select mode="tags" placeholder={t('allergies')} />
                </Form.Item>

                <Form.Item
                    name="chronicDiseases"
                    label={t('chronicDiseases')}
                    className="ant-form-item-full"
                >
                    <Select mode="tags" placeholder={t('chronicDiseases')} />
                </Form.Item>

                <Form.Item
                    name="pastSurgicalHistory"
                    label={t('pastSurgicalHistory')}
                    className="ant-form-item-full"
                >
                    <Input.TextArea rows={2} placeholder={t('pastSurgicalHistory')} />
                </Form.Item>

                <Form.Item
                    name="familyHistoryFather"
                    label={t('familyHistoryFather')}
                    className="ant-form-item-full"
                >
                    <Input.TextArea rows={2} placeholder={t('familyHistoryFather')} />
                </Form.Item>

                <Form.Item
                    name="familyHistoryMother"
                    label={t('familyHistoryMother')}
                    className="ant-form-item-full"
                >
                    <Input.TextArea rows={2} placeholder={t('familyHistoryMother')} />
                </Form.Item>

                <Form.Item
                    name="notes"
                    label={t('notes')}
                    className="ant-form-item-full"
                >
                    <Input.TextArea placeholder={t('notes')} rows={3} />
                </Form.Item>
            </FormModal>

            <Modal
                title={`${t('orderLabTest') || 'Order Lab Test'}${orderPatient ? ` — ${orderPatient.name}` : ''}`}
                open={orderModalVisible}
                onCancel={() => setOrderModalVisible(false)}
                onOk={() => orderForm.submit()}
                confirmLoading={orderSubmitting}
                okText={
                    selectedCatalogIds.length > 1
                        ? `${t('orderTests') || 'Order Tests'} (${selectedCatalogIds.length})`
                        : (t('orderTest') || 'Order Test')
                }
                destroyOnClose
            >
                {catalog.length === 0 ? (
                    <p style={{ color: '#888' }}>
                        {t('noOrderableTests') ||
                            'No tests to order yet. Switch on the ones you use under Settings > Lab tests I order, or ask your lab to add them to its catalog.'}
                    </p>
                ) : (
                    <Form form={orderForm} layout="vertical" onFinish={handleOrderSubmit}>
                        <Form.Item
                            label={t('tests') || 'Tests'}
                            help={t('selectMultipleTestsHint') || 'You can pick several tests at once'}
                        >
                            <Select
                                mode="multiple"
                                allowClear
                                showSearch
                                value={selectedCatalogIds}
                                onChange={setSelectedCatalogIds}
                                placeholder={t('selectTests') || 'Select one or more tests'}
                                optionFilterProp="label"
                                options={labTestSelectOptions(catalog)}
                            />
                        </Form.Item>

                        {selectedTests.length > 0 && (
                            <div style={{ marginBottom: 16 }}>
                                {selectedTests.map((item) => (
                                    <div key={item.value} style={{ marginBottom: 4 }}>
                                        <Tag color="blue">{t(item.testType) || item.testType}</Tag>
                                        <strong>{item.testName}</strong>
                                        {item.referenceRange && (
                                            <span style={{ color: '#888', marginInlineStart: 8 }}>
                                                {t('referenceRange')}: {item.referenceRange}
                                            </span>
                                        )}
                                    </div>
                                ))}
                                {totalPrice > 0 && (
                                    <div style={{ marginTop: 8 }}>
                                        <strong>{t('total')}: {totalPrice} IQD</strong>
                                    </div>
                                )}
                            </div>
                        )}

                        <Form.Item name="notes" label={t('notes')}>
                            <Input.TextArea rows={2} placeholder={t('notesAppliedToAllTests') || 'Applied to every selected test'} />
                        </Form.Item>
                    </Form>
                )}
            </Modal>

            <DuplicatePatientModal
                open={duplicateModalOpen}
                matches={duplicates}
                creating={creating}
                onSelect={openExistingPatient}
                onCreateAnyway={() => pendingValues && submitPatient(pendingValues, { force: true })}
                onCancel={() => setDuplicateModalOpen(false)}
            />
        </div>
    );
};

export default Patients;
